import { useCallback, useEffect, useState } from 'react';

import { useGetDistinctFilterValuesMutation } from '@/entities/pattern';

import { sortTimeframesChronologically } from '@/shared/lib/financial';
import { logError } from '@/shared/lib/logging';
import { roundArrayOfStringNumbers } from '@/shared/lib/math';

interface FilterOptionsState {
    isLoading: boolean;
    tickerOptions: string[];
    timeframeOptions: string[];
    lengthOptions: string[];
    thresholdOptions: string[];
    directionOptions: { label: string; value: string }[];
}

export const useFilterOptions = (t: (key: string) => string) => {
    const [getDistinctValues] = useGetDistinctFilterValuesMutation();

    const [options, setOptions] = useState<FilterOptionsState>({
        isLoading: true,
        tickerOptions: [],
        timeframeOptions: [],
        lengthOptions: [],
        thresholdOptions: [],
        directionOptions: [
            { label: t('filters.all'), value: '' },
            { label: t('filters.bearish'), value: '0' },
            { label: t('filters.bullish'), value: '1' },
        ],
    });

    const fetchFilterOptions = useCallback(async () => {
        try {
            const result = await getDistinctValues([
                'Ticker',
                'Timeframe',
                'pattern_len_',
                'price_threshold_post_mean',
            ]).unwrap();

            // Get timeframes and sort them if they exist
            const timeframes = result['Timeframe'] || [];
            const sortedTimeframes =
                timeframes.length > 0
                    ? sortTimeframesChronologically([...timeframes])
                    : timeframes;

            setOptions({
                isLoading: false,
                tickerOptions: result['Ticker'] || [],
                timeframeOptions: sortedTimeframes,
                lengthOptions: result['pattern_len_']
                    ? roundArrayOfStringNumbers(result['pattern_len_'])
                    : [],
                thresholdOptions: result['price_threshold_post_mean']
                    ? roundArrayOfStringNumbers(
                          result['price_threshold_post_mean'],
                      )
                    : [],
                directionOptions: [
                    { label: t('filters.all'), value: '' },
                    { label: t('filters.bearish'), value: '0' },
                    { label: t('filters.bullish'), value: '1' },
                ],
            });
        } catch (error) {
            logError('Failed to fetch filter options:', error);

            setOptions((prev) => ({ ...prev, isLoading: false }));
        }
    }, [getDistinctValues, t]);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    return options;
};
